import React, { useEffect } from 'react';
import { useAppDispatch, useAppSelector } from 'src/store/store';
import {
  addOrEditSite,
  setIsCreateModalOpen,
  setSelectedSite,
  setIsLoading,
} from 'src/store/adminConfigPageSlice/siteConfigPageSlice/siteConfigPageSlice';
import Modal, { ModalFooter } from '@amzn/meridian/modal';
import Row from '@amzn/meridian/row';
import Button from '@amzn/meridian/button';
import useForm from 'src/hooks/useForm';
import SiteFormFields from './siteFormFields/siteFormFields';
import { TableDataRow } from 'src/store/adminConfigPageSlice/siteConfigPageSlice/types';
import { getAllowedSites } from 'src/store/siteSlice';

const SiteModal = () => {
  const dispatch = useAppDispatch();
  const { isCreateModalOpen, selectedSite, siteModalFieldLengths } = useAppSelector((state) => state.siteConfigPage);
  const { validateForm, handleChange, resetForm, setFormValues, formData, errors } = useForm<TableDataRow>({
    validations: {
      site: {
        required: {
          value: true,
          message: 'Site name is required',
        },
        custom: {
          isValid: (value) => value.length <= siteModalFieldLengths.maxSiteCharLength,
          message: `Site name must be less than or equal to ${siteModalFieldLengths.maxSiteCharLength} characters.`,
        },
      },
      country: {
        required: {
          value: true,
          message: 'Country is required',
        },
      },
      region: {
        required: {
          value: true,
          message: 'Region is required',
        },
      },
      org: {
        required: {
          value: true,
          message: 'Org is required',
        },
      },
      buildingType: {
        required: {
          value: true,
          message: 'Building Type is required',
        },
      },
      description: {
        required: {
          value: false,
        },
        custom: {
          isValid: (value) => value.length <= siteModalFieldLengths.maxDescriptionCharLength,
          message: `Description must be less than or equal to ${siteModalFieldLengths.maxDescriptionCharLength} characters.`,
        },
      },
    },
    initialValues: {
      isActive: false,
      site: '',
      description: '',
    },
  });

  useEffect(() => {
    if (selectedSite) {
      setFormValues(selectedSite);
    }
  }, [selectedSite]);

  const onSave = async () => {
    if (validateForm()) {
      dispatch(setIsLoading(true));
      // wait for site changes to be persisted
      await dispatch(addOrEditSite(formData));
      // update allowedSites which tends to be the source of truth for site configurations.
      // this update is redundant in the current implementation and was added for future-proofing
      dispatch(getAllowedSites());
      dispatch(setIsCreateModalOpen(false));
      dispatch(setSelectedSite(undefined));
      resetForm();
    }
  };

  const onCancel = () => {
    dispatch(setIsCreateModalOpen(false));
    dispatch(setSelectedSite(undefined));
    resetForm();
  };

  return (
    <Modal
      title={selectedSite ? 'Edit Site' : 'Add New Site'}
      open={isCreateModalOpen}
      onClose={onCancel}
      closeLabel="Close"
      aria-describedby="modal-description"
      width="700px"
      scrollContainer="modal"
    >
      <SiteFormFields formData={formData} errors={errors} handleChange={handleChange} />
      <ModalFooter>
        <Row alignmentHorizontal="end" widths="fill">
          <Button type="secondary" data-cy="site-modal-cancel-btn" size="small" onClick={onCancel}>
            Cancel
          </Button>
          <Button id="site-modal-save-btn" data-cy="site-modal-save-btn" type="primary" size="small" onClick={onSave}>
            Save
          </Button>
        </Row>
      </ModalFooter>
    </Modal>
  );
};

export default SiteModal;
